/**
 * AiMemoryCard — the user's window into everything Fias AI remembers
 * (the ChatGPT memory-management contract): the master switch, the three
 * layers (instructions / when-I-say-X rules / learned), per-entry edit,
 * enable/disable, delete, and delete-all. Mounted on Account Settings;
 * the assistant window's gear links here.
 *
 * Grouped by where each memory applies (ADR-039 prerequisite 2): everywhere
 * (what the user adds here), the personal vault, and each workspace — what
 * Fias AI saves applies only in the workspace it was working in. A workspace
 * the user can no longer access shows as a name and a count only; its memories
 * are deleted automatically 30 days later.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Brain, Plus, Trash2, Pencil, Check, X, Lock } from 'lucide-react';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

type MemoryKind = 'instruction' | 'rule' | 'learned';

interface MemoryRow {
  memoryId: string;
  kind: MemoryKind;
  title: string | null;
  content: string;
  source: 'user' | 'assistant';
  holder: { holderId: string; holderType: 'personal' | 'org' } | null;
  holderName: string | null;
  enabled: boolean;
  /** Learned from a label the user can no longer read: no words, delete only. */
  hiddenByLabel?: true;
}

interface UnreachableGroup {
  holderId: string;
  holderName: string;
  count: number;
  /** When they are deleted automatically, unless access comes back. */
  deletesAt: string;
}

/** A bulk delete waiting for the person to confirm it. */
type PendingDelete = { kind: 'workspace'; group: UnreachableGroup } | { kind: 'all' };

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

interface ScopeGroup {
  key: string;
  heading: string;
  hint: string;
  memories: MemoryRow[];
  /** Only "Everywhere" takes new entries: what the user types applies everywhere. */
  canAdd: boolean;
}

/** Everywhere first, then the personal vault, then workspaces by name. */
function groupByScope(memories: MemoryRow[]): ScopeGroup[] {
  const everywhere: ScopeGroup = {
    key: 'everywhere',
    heading: 'Everywhere',
    hint: 'What you add here applies in your personal vault and every workspace.',
    memories: [],
    canAdd: true,
  };
  const personal: ScopeGroup = {
    key: 'personal',
    heading: 'Personal vault',
    hint:
      'Saved while Fias AI worked in your personal vault; used only there. Includes ' +
      'what Fias AI saved before memories were kept per workspace.',
    memories: [],
    canAdd: false,
  };
  const workspaces = new Map<string, ScopeGroup>();
  for (const m of memories) {
    if (!m.holder) everywhere.memories.push(m);
    else if (m.holder.holderType === 'personal') personal.memories.push(m);
    else {
      const name = m.holderName ?? 'Workspace';
      const group = workspaces.get(m.holder.holderId) ?? {
        key: m.holder.holderId,
        heading: name,
        hint: `Saved while Fias AI worked in ${name}; used only there.`,
        memories: [],
        canAdd: false,
      };
      group.memories.push(m);
      workspaces.set(m.holder.holderId, group);
    }
  }
  // Only where it can apply: the migration turned off old assistant memories
  // that may be about a workspace, and a new user has none.
  if (personal.memories.some((m) => m.source === 'assistant' && !m.enabled)) {
    personal.hint +=
      ' Ones Fias AI saved before then while you belonged to a workspace were turned off, ' +
      'since they may be about that workspace. Turn one back on if it is about you.';
  }
  const named = [...workspaces.values()].sort((a, b) => a.heading.localeCompare(b.heading));
  return [everywhere, ...(personal.memories.length > 0 ? [personal] : []), ...named];
}

const KIND_META: Record<MemoryKind, { heading: string; hint: string; addLabel: string }> = {
  instruction: {
    heading: 'Instructions',
    hint: 'Standing guidance Fias AI follows in every conversation.',
    addLabel: 'Add instruction',
  },
  rule: {
    heading: 'Rules',
    hint: '“When I say X, do Y” shorthands (e.g. “EOD means 6pm”).',
    addLabel: 'Add rule',
  },
  learned: {
    heading: 'Learned by Fias AI',
    hint: 'Saved during conversations — always visible here, never silent.',
    addLabel: 'Add memory',
  },
};

export function AiMemoryCard() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.fiasAI.memory(),
    queryFn: () => api.getAssistantMemory(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.fiasAI.memory() });

  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) => api.setAssistantMemoryEnabled(enabled),
    onSuccess: invalidate,
  });
  const createMemory = useMutation({
    mutationFn: (input: { kind: MemoryKind; content: string }) => api.createAssistantMemory(input),
    onSuccess: invalidate,
  });
  const updateMemory = useMutation({
    mutationFn: (input: { memoryId: string; content?: string; enabled?: boolean }) =>
      api.updateAssistantMemory(input.memoryId, {
        content: input.content,
        enabled: input.enabled,
      }),
    onSuccess: invalidate,
  });
  const deleteMemory = useMutation({
    mutationFn: (memoryId: string) => api.deleteAssistantMemory(memoryId),
    onSuccess: invalidate,
  });
  const deleteWorkspace = useMutation({
    mutationFn: (holderId: string) => api.deleteAssistantMemoriesInWorkspace(holderId),
    onSuccess: invalidate,
  });
  const deleteAll = useMutation({
    mutationFn: () => api.deleteAllAssistantMemories(),
    onSuccess: invalidate,
  });

  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const confirmDelete = () => {
    const pending = pendingDelete;
    setPendingDelete(null);
    if (pending?.kind === 'workspace') deleteWorkspace.mutate(pending.group.holderId);
    else if (pending?.kind === 'all') deleteAll.mutate();
  };

  const memories = (data?.memories ?? []) as MemoryRow[];
  const unreachable = (data?.unreachable ?? []) as UnreachableGroup[];
  const enabled = data?.enabled ?? true;

  return (
    <Card data-testid="ai-memory-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5" /> Fias AI Memory
            </CardTitle>
            <CardDescription>
              Everything Fias AI remembers about you — yours to edit, disable, or delete. Memory
              never changes safety rules: risky actions always ask you first.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Memory</span>
            <Switch
              checked={enabled}
              onCheckedChange={(v) => setEnabled.mutate(v)}
              aria-label="Memory master switch"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            {!enabled && (
              <p className="text-sm text-muted-foreground rounded-md bg-muted px-3 py-2">
                Memory is off — Fias AI won’t use or save memories, but your entries are kept and
                you can still manage them below.
              </p>
            )}
            {groupByScope(memories).map((group) => (
              <section
                key={group.key}
                className="space-y-4"
                data-testid={`memory-scope-${group.key}`}
                aria-label={group.heading}
              >
                <div>
                  <h2 className="text-base font-semibold">{group.heading}</h2>
                  <p className="text-xs text-muted-foreground">{group.hint}</p>
                </div>
                {(Object.keys(KIND_META) as MemoryKind[]).map((kind) => {
                  const entries = group.memories.filter((m) => m.kind === kind);
                  if (!group.canAdd && entries.length === 0) return null;
                  return (
                    <MemorySection
                      key={kind}
                      kind={kind}
                      memories={entries}
                      onAdd={
                        group.canAdd
                          ? (content) => createMemory.mutate({ kind, content })
                          : undefined
                      }
                      onEdit={(memoryId, content) => updateMemory.mutate({ memoryId, content })}
                      onToggle={(memoryId, on) => updateMemory.mutate({ memoryId, enabled: on })}
                      onDelete={(memoryId) => deleteMemory.mutate(memoryId)}
                    />
                  );
                })}
              </section>
            ))}
            {unreachable.length > 0 && (
              <section
                className="space-y-2"
                data-testid="memory-scope-unreachable"
                aria-label="Workspaces you no longer have access to"
              >
                <div>
                  <h2 className="text-base font-semibold">
                    Workspaces you no longer have access to
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    Fias AI no longer uses these, and their content is hidden because you can no
                    longer access the workspace. They are deleted automatically on the date shown
                    unless you regain access, or you can delete them now.
                  </p>
                </div>
                <ul className="space-y-1.5">
                  {unreachable.map((g) => (
                    <li
                      key={g.holderId}
                      className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
                    >
                      <Lock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="flex-1 min-w-0 break-words">
                        {g.holderName || 'A workspace'} · {g.count}{' '}
                        {g.count === 1 ? 'memory' : 'memories'}
                        <span className="text-muted-foreground">
                          {' '}
                          · deleted {formatDay(g.deletesAt)}
                        </span>
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => setPendingDelete({ kind: 'workspace', group: g })}
                        disabled={deleteWorkspace.isPending}
                      >
                        Delete these
                      </Button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {(memories.length > 0 || unreachable.length > 0) && (
              <div className="pt-2 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  onClick={() => setPendingDelete({ kind: 'all' })}
                  disabled={deleteAll.isPending}
                >
                  <Trash2 className="h-4 w-4 mr-1.5" /> Delete all memory
                </Button>
              </div>
            )}
          </>
        )}
        {/* Never window.confirm: a browser can suppress native dialogs, and then
            confirm() returns false at once, so the button silently does nothing. */}
        <AlertDialog
          open={pendingDelete !== null}
          onOpenChange={(o) => !o && setPendingDelete(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {pendingDelete?.kind === 'workspace'
                  ? `Delete ${pendingDelete.group.count} ${
                      pendingDelete.group.count === 1 ? 'memory' : 'memories'
                    } from ${pendingDelete.group.holderName || 'this workspace'}?`
                  : 'Delete all memory?'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {pendingDelete?.kind === 'workspace'
                  ? 'Their content is hidden, so you cannot review them first. This cannot be undone.'
                  : 'Everything listed here is deleted, including memories from workspaces you no longer have access to. This cannot be undone.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className={buttonVariants({ variant: 'destructive' })}
                onClick={confirmDelete}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

function MemorySection({
  kind,
  memories,
  onAdd,
  onEdit,
  onToggle,
  onDelete,
}: {
  kind: MemoryKind;
  memories: MemoryRow[];
  /** Absent where new entries cannot be added (only "Everywhere" takes them). */
  onAdd?: (content: string) => void;
  onEdit: (memoryId: string, content: string) => void;
  onToggle: (memoryId: string, enabled: boolean) => void;
  onDelete: (memoryId: string) => void;
}) {
  const meta = KIND_META[kind];
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">{meta.heading}</h3>
          <p className="text-xs text-muted-foreground">{meta.hint}</p>
        </div>
        {onAdd && (
          <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4 mr-1" /> {meta.addLabel}
          </Button>
        )}
      </div>

      {adding && onAdd && (
        <div className="mt-2 space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={2000}
            rows={2}
            placeholder={kind === 'rule' ? 'When I say …, do …' : 'What should Fias AI remember?'}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => {
                if (draft.trim()) onAdd(draft.trim());
                setDraft('');
                setAdding(false);
              }}
            >
              <Check className="h-4 w-4 mr-1" /> Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <ul className="mt-2 space-y-1.5">
        {memories.length === 0 && !adding && (
          <li className="text-xs text-muted-foreground italic">Nothing here yet.</li>
        )}
        {memories.map((m) => (
          <li
            key={m.memoryId}
            className="flex items-start gap-2 rounded-md border border-border px-3 py-2 text-sm"
          >
            {editingId === m.memoryId ? (
              <div className="flex-1 space-y-2">
                <Textarea
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  maxLength={2000}
                  rows={2}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      if (editDraft.trim()) onEdit(m.memoryId, editDraft.trim());
                      setEditingId(null);
                    }}
                  >
                    <Check className="h-4 w-4 mr-1" /> Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : m.hiddenByLabel ? (
              <>
                <span className="flex-1 min-w-0 break-words italic text-muted-foreground">
                  Learned from a labelled document you can no longer see, so it’s hidden and Fias AI
                  doesn’t use it.
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 flex-shrink-0 text-destructive"
                  onClick={() => onDelete(m.memoryId)}
                  title="Delete"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </>
            ) : (
              <>
                <span
                  className={`flex-1 min-w-0 break-words ${m.enabled ? '' : 'line-through text-muted-foreground'}`}
                >
                  {m.title ? <span className="font-medium">{m.title}: </span> : null}
                  {m.content}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Switch
                    checked={m.enabled}
                    onCheckedChange={(v) => onToggle(m.memoryId, v)}
                    aria-label="Enable memory entry"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => {
                      setEditingId(m.memoryId);
                      setEditDraft(m.content);
                    }}
                    title="Edit"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive"
                    onClick={() => onDelete(m.memoryId)}
                    title="Delete"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
